import { useEffect, useRef, useState, type ReactNode } from 'react';
import '@prism-bastion/web-shared/i18n';
import { TUTORIAL_LEVEL_ID, getLevel } from '@prism-bastion/game-core/game/config';
import { DEFAULT_DIFFICULTY_ID } from '@prism-bastion/game-core/game/difficulty';
import { GameEngine } from '@prism-bastion/game-core/game/engine';
import type { SignalId } from '@prism-bastion/game-core/game/types';
import { DEFAULT_SIGNAL_ID } from '@prism-bastion/game-core/signals';
import { SignalArchive } from './SignalArchive';
import { DefenseArchive } from './DefenseArchive';
import { GameSession } from './GameSession';
import { LevelSelect, type LevelSelection } from './LevelSelect';
import { TutorialOffer } from './TutorialOffer';
import { defenseArchiveRepository } from './defense-archive';
import { getAutoPauseEnabled, useAutoPauseEnabled } from '@prism-bastion/web-shared/ui/preferences';
import { ThoughtIndex } from '@prism-bastion/web-shared/ui/ThoughtIndex';
import { createWebVisualFeedback } from '@prism-bastion/web-shared/visual-feedback';
import styles from './App.module.css';

export const TUTORIAL_OFFER_STORAGE_KEY = 'prism-bastion-tutorial-offer-resolved';

const tutorialOfferWasResolved = (): boolean => {
	try {
		return globalThis.localStorage?.getItem(TUTORIAL_OFFER_STORAGE_KEY) === '1';
	} catch {
		return false;
	}
};

const rememberTutorialOfferResolution = (): void => {
	try {
		globalThis.localStorage?.setItem(TUTORIAL_OFFER_STORAGE_KEY, '1');
	} catch {
		// Storage may be unavailable in privacy-restricted browser contexts.
	}
};

const tutorialSelection = (): LevelSelection => ({
	levelId: TUTORIAL_LEVEL_ID,
	mode: 'standard',
	difficultyId: DEFAULT_DIFFICULTY_ID,
	creative: {
		healthScale: 1,
		speedScale: 1,
		coreStability: 20,
		waveCount: getLevel(TUTORIAL_LEVEL_ID).waves.length,
	},
});

export interface SinglePlayerAppProps {
	homeActions?: ReactNode;
}

export function SinglePlayerApp({ homeActions }: SinglePlayerAppProps = {}) {
	const [engine, setEngine] = useState<GameEngine | null>(null);
	const [archiveType, setArchiveType] = useState<SignalId | null>(null);
	const [defenseArchiveOpen, setDefenseArchiveOpen] = useState(false);
	const [tutorialOfferOpen, setTutorialOfferOpen] = useState(() => !tutorialOfferWasResolved());
	const [thoughtOpen, setThoughtOpen] = useState<{ id?: string } | null>(null);
	const thoughtOriginRef = useRef<HTMLElement | null>(null);
	const autoPauseEnabled = useAutoPauseEnabled();
	const start = (selection: LevelSelection): void => {
		const nextEngine = new GameEngine({ ...selection, visuals: createWebVisualFeedback() });
		nextEngine.setAutoPauseEnabled(getAutoPauseEnabled());
		setEngine(nextEngine);
	};
	useEffect(() => {
		engine?.setAutoPauseEnabled(autoPauseEnabled);
	}, [autoPauseEnabled, engine]);
	useEffect(() => {
		if (!engine) {
			return;
		}
		let windowBlurred = false;
		let documentHidden = document.visibilityState !== 'visible';
		const syncPageFocus = (): void => engine.setAutoPauseCondition('page-focus', windowBlurred || documentHidden);
		const onBlur = (): void => {
			windowBlurred = true;
			syncPageFocus();
		};
		const onFocus = (): void => {
			windowBlurred = false;
			syncPageFocus();
		};
		const onVisibilityChange = (): void => {
			documentHidden = document.visibilityState !== 'visible';
			syncPageFocus();
		};
		window.addEventListener('blur', onBlur);
		window.addEventListener('focus', onFocus);
		document.addEventListener('visibilitychange', onVisibilityChange);
		syncPageFocus();
		return () => {
			window.removeEventListener('blur', onBlur);
			window.removeEventListener('focus', onFocus);
			document.removeEventListener('visibilitychange', onVisibilityChange);
			engine.setAutoPauseCondition('page-focus', false);
		};
	}, [engine]);
	const openSignalArchive = (type: SignalId): void => {
		engine?.setAutoPauseCondition('signal-archive', true);
		setArchiveType(type);
	};
	const closeSignalArchive = (): void => {
		engine?.setAutoPauseCondition('signal-archive', false);
		setArchiveType(null);
	};
	const openThought = (id?: string): void => {
		thoughtOriginRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
		engine?.setAutoPauseCondition('thought-index', true);
		setThoughtOpen(id ? { id } : {});
	};
	const closeThought = (): void => {
		engine?.setAutoPauseCondition('thought-index', false);
		setThoughtOpen(null);
		requestAnimationFrame(() => thoughtOriginRef.current?.focus());
	};
	const declineTutorial = (): void => {
		rememberTutorialOfferResolution();
		setTutorialOfferOpen(false);
	};
	const acceptTutorial = (): void => {
		setTutorialOfferOpen(false);
		start(tutorialSelection());
	};
	const renderContent = (): ReactNode => {
		if (defenseArchiveOpen) {
			return <DefenseArchive repository={defenseArchiveRepository} onBack={() => setDefenseArchiveOpen(false)} />;
		}
		if (archiveType) {
			return (
				<SignalArchive
					initialType={archiveType}
					onBack={closeSignalArchive}
					backToBattlefield={Boolean(engine)}
				/>
			);
		}
		if (engine) {
			return (
				<GameSession
					engine={engine}
					suspended={Boolean(thoughtOpen)}
					defenseArchive={defenseArchiveRepository}
					onExit={() => setEngine(null)}
					onOpenArchive={openSignalArchive}
					onOpenThought={openThought}
					onTutorialResolved={rememberTutorialOfferResolution}
				/>
			);
		}
		return (
			<LevelSelect
				homeActions={homeActions}
				onStart={start}
				onOpenArchive={() => setArchiveType(DEFAULT_SIGNAL_ID)}
				onOpenDefenseArchive={() => setDefenseArchiveOpen(true)}
				onOpenThought={() => openThought()}
			/>
		);
	};
	const content = renderContent();
	return (
		<>
			<div
				className={thoughtOpen ? styles.contentCovered : undefined}
				inert={Boolean(thoughtOpen)}
				aria-hidden={thoughtOpen ? true : undefined}
			>
				{content}
				{tutorialOfferOpen && !engine && !archiveType && !defenseArchiveOpen ? (
					<TutorialOffer onAccept={acceptTutorial} onDecline={declineTutorial} />
				) : null}
			</div>
			{thoughtOpen ? (
				<ThoughtIndex
					{...(thoughtOpen.id ? { initialThoughtId: thoughtOpen.id } : {})}
					onBack={closeThought}
					backToBattlefield={Boolean(engine)}
				/>
			) : null}
		</>
	);
}

export const App = SinglePlayerApp;
